import React from 'react'
import { List, useTable, EditButton } from '@refinedev/antd'
import { useUpdate, useList } from '@refinedev/core'
import { Table, Space, Switch, Tag } from 'antd'
import { brand } from '../../theme/brand'
import { ListPage, SectionCard, TableSurface, type Stat } from '../../theme/listkit'
import { Settings, ShieldCheck, Activity, Bell } from '../../components/icons'
import { isEventWired } from './catalog'

interface NotificationRule {
  id: string
  name: string
  enabled: boolean
  trigger_event: string
  create_activity: boolean
  create_notification: boolean
  priority: number
  updated_at: string
}

/** A single explainer panel inside the "How feed rules work" card. */
function DefinitionPanel({
  icon: Icon,
  tone,
  eyebrow,
  heading,
  children,
}: {
  icon: typeof Activity
  tone: { fg: string; chipBg: string; chipBorder: string }
  eyebrow: string
  heading: string
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: brand.paper,
        border: '1px solid ' + brand.cardLine,
        borderRadius: 12,
        borderTop: '3px solid ' + tone.fg,
        padding: '16px 18px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 9,
            background: tone.chipBg,
            border: '1px solid ' + tone.chipBorder,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon size={17} color={tone.fg} />
        </div>
        <div>
          <div
            style={{
              fontFamily: brand.fSans,
              fontWeight: 700,
              fontSize: 9.5,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: tone.fg,
              marginBottom: 1,
            }}
          >
            {eyebrow}
          </div>
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 16, color: brand.ink }}>
            {heading}
          </div>
        </div>
      </div>
      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 12.5, color: brand.sub, lineHeight: 1.62 }}>
        {children}
      </div>
    </div>
  )
}

/** Notification rules — declarative event → activity/notification mappings. */
export function NotificationRuleList() {
  const { tableProps } = useTable<NotificationRule>({
    resource: 'notification_rules',
    syncWithLocation: true,
    sorters: { initial: [{ field: 'priority', order: 'asc' }] },
  })
  const { mutate: update } = useUpdate()

  // Summary-deck counts come from BOUNDED pageSize:1 queries: PostgREST returns
  // the exact `total` for each filter without shipping any rows, so the deck no
  // longer fetches the whole table to count it on the main thread. Each tiny
  // query runs in parallel. There is no admin_notification_rules_summary view,
  // and these are plain COUNT(filter) numbers, so count queries are the right
  // tool. liveMode 'off' avoids a second realtime subscription on a resource
  // useTable already subscribes to.
  const { result: totalRes } = useList<NotificationRule>({
    resource: 'notification_rules',
    pagination: { pageSize: 1 },
    liveMode: 'off',
  })
  const { result: enabledRes } = useList<NotificationRule>({
    resource: 'notification_rules',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    filters: [{ field: 'enabled', operator: 'eq', value: true }],
  })
  const { result: activityRes } = useList<NotificationRule>({
    resource: 'notification_rules',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    filters: [{ field: 'create_activity', operator: 'eq', value: true }],
  })
  const { result: notificationRes } = useList<NotificationRule>({
    resource: 'notification_rules',
    pagination: { pageSize: 1 },
    liveMode: 'off',
    filters: [{ field: 'create_notification', operator: 'eq', value: true }],
  })
  const totalCount = totalRes?.total ?? 0
  const enabledCount = enabledRes?.total ?? 0
  const activityCount = activityRes?.total ?? 0
  const notificationCount = notificationRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Total Rules', value: totalCount.toLocaleString(), icon: Settings, tone: 'jaguar' },
    { label: 'Enabled', value: enabledCount.toLocaleString(), hint: 'live', icon: ShieldCheck, tone: 'green' },
    { label: 'Activities', value: activityCount.toLocaleString(), icon: Activity, tone: 'gold' },
    { label: 'Notifications', value: notificationCount.toLocaleString(), icon: Bell, tone: 'green' },
  ]

  const GOLD = { fg: brand.gold, chipBg: 'rgba(196,151,49,.12)', chipBorder: 'rgba(196,151,49,.30)' }
  const GREEN = { fg: '#1f7a4d', chipBg: '#e3f3ea', chipBorder: 'rgba(31,122,77,.25)' }

  return (
    <ListPage
      intro={{
        eyebrow: 'Messaging · Notification Rules',
        description:
          'These declarative rules turn app events into public feed activities and private member notifications — flip a switch to change what the network sees and who gets pinged.',
      }}
      stats={stats}
    >
      <SectionCard
        title="How feed rules work"
        sub="Every rule can emit either signal — or both — when its trigger fires."
        style={{ marginBottom: 18 }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
          <DefinitionPanel icon={Activity} tone={GOLD} eyebrow="Public · Shared" heading="Activities">
            An activity is a public entry posted to the in-app Activity Feed that the whole targeted audience
            sees — for example, &ldquo;David Charles &rsquo;91 gave to the Scholarship Fund.&rdquo; It is the shared,
            social signal that keeps the community aware of what&rsquo;s happening across the network.
          </DefinitionPanel>
          <DefinitionPanel icon={Bell} tone={GREEN} eyebrow="Private · Personal" heading="Notifications">
            A notification is a private, per-member message delivered straight to one member&rsquo;s notifications
            inbox (with an optional push) — for example, a personal thank-you sent only to the donor. It is the
            targeted, individual signal meant for one recipient rather than the whole feed.
          </DefinitionPanel>
        </div>
      </SectionCard>

      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }}>
            <Table.Column
              dataIndex="enabled"
              title="Enabled"
              render={(value: boolean, record: NotificationRule) => (
                <Switch
                  checked={value}
                  onChange={(checked) =>
                    update({ resource: 'notification_rules', id: record.id, values: { enabled: checked } })
                  }
                />
              )}
            />
            <Table.Column dataIndex="name" title="Name" />
            <Table.Column
              dataIndex="trigger_event"
              title="When"
              render={(v: string) => (
                <Space size={4} wrap>
                  <Tag color="blue">{v}</Tag>
                  {!isEventWired(v) && (
                    <Tag color="orange" title="No emitter fires this event in production — the rule stays inert until one goes live.">
                      not wired
                    </Tag>
                  )}
                </Space>
              )}
            />
            <Table.Column
              title="Creates"
              dataIndex="creates"
              render={(_, r: NotificationRule) => (
                <Space>
                  {r.create_activity && <Tag color="gold">Activity</Tag>}
                  {r.create_notification && <Tag color="green">Notification</Tag>}
                </Space>
              )}
            />
            <Table.Column dataIndex="priority" title="Priority" />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              render={(_, record: NotificationRule) => (
                <Space>
                  <EditButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
